import { commonUtils } from '@/helpers/commonUtils'
import styles from './PptPageHeatMapFrame.module.less'
import { useTranslation } from 'react-i18next'
import PptPage, { PageHeader } from '../PptPage/PptPage'
import { SliderModels } from '@/types/slider'
import { useEffect, useRef, useState } from 'react'
import cls from 'classnames'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'

interface Props {
  project: ProjectModels.ProjectDetail
  heatMaps: Array<{ heatMap: string; second: number }>
  onSliderReady: (slider: SliderModels.Slider, no: number) => void
  no: number
}

function PptPageHeatMapFrame({ project, heatMaps, onSliderReady, no }: Props) {
  const { t } = useTranslation()
  const [basicSlider, setBasicSlider] = useState<SliderModels.Slider>()
  const [elements, setElements] = useState<SliderModels.PPTElement[]>([])
  const [pageHeaderElements, setPageHeaderElements] = useState<SliderModels.PPTElement[]>([])
  const [pageRect, setPageRect] = useState<DOMRect>()
  const isPortraitLayout = isPortraitByProjectInfo(project)

  const onPageRender = (slider: SliderModels.Slider, pageRect: DOMRect) => {
    setBasicSlider(slider)
    setPageRect(pageRect)
  }
  const onPageHeaderRender = (elements: SliderModels.PPTElement[]) => {
    setPageHeaderElements(elements)
  }
  const onElementReady = (els: SliderModels.PPTElement[]) => {
    setElements((elements) => {
      return [...elements, ...els]
    })
  }
  useEffect(() => {
    if (
      !pageRect ||
      !basicSlider ||
      pageHeaderElements.length === 0 ||
      elements.length === 0 ||
      elements.length !== heatMaps.length * 2
    ) {
      return
    }
    const _pageHeaderElements = pageHeaderElements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })
    const _elements = elements.map((element) => {
      const { x, y } = element
      return {
        ...element,
        x: x - pageRect.left,
        y: y - pageRect.top,
      }
    })

    const slider: SliderModels.Slider = {
      ...basicSlider,
      elements: [..._pageHeaderElements, ...basicSlider.elements, ..._elements],
    }
    onSliderReady(slider, no)
  }, [pageRect, basicSlider, pageHeaderElements, elements, heatMaps.length, onSliderReady, no])

  return (
    <PptPage
      bgSrc={commonUtils.getImagePath('PDFDownload/pdf-page-heat-map/PDF-heat-map-bg.jpg')}
      no={no}
      onPageRender={onPageRender}
    >
      <div className={styles.pptPageHeatMapFrame}>
        <PageHeader
          project={project}
          text={'04.' + t('dashboard.projectDetailPage.heatmapAnalysis')}
          onRender={onPageHeaderRender}
        />
        <div className={cls(styles.content, { [styles.portraitContent]: isPortraitLayout })}>
          {heatMaps.map((hm) => {
            return (
              <FrameItem
                key={`${hm.heatMap}-${hm.second}`}
                hm={hm}
                onElementReady={onElementReady}
                isPortraitLayout={isPortraitLayout}
              />
            )
          })}
        </div>
      </div>
    </PptPage>
  )
}

function FrameItem(props: {
  hm: { heatMap: string; second: number }
  onElementReady: (elements: SliderModels.PPTElement[]) => void
  isPortraitLayout: boolean
}) {
  const { hm, onElementReady, isPortraitLayout } = props
  const titleRef = useRef<HTMLSpanElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  useEffect(() => {
    setTimeout(() => {
      if (!titleRef.current || !imageRef.current || !isImageLoaded) {
        return
      }
      const titleRect = titleRef.current.getBoundingClientRect()
      const imageRect = imageRef.current.getBoundingClientRect()
      const elements: SliderModels.PPTElement[] = [
        {
          type: 'text',
          content: titleRef.current.outerHTML,
          x: titleRect.x + 10,
          y: titleRect.y - 55,
          width: titleRect.width,
          height: titleRect.height,
        },
        {
          type: 'image',
          src: hm.heatMap,
          x: imageRect.x,
          y: imageRect.y,
          width: imageRect.width,
          height: imageRect.height,
        },
      ]
      onElementReady(elements)
    }, 1000)
  }, [isImageLoaded])

  return (
    <div key={hm.heatMap} className={styles.frameItem}>
      <p className={styles.title}>
        <span
          ref={titleRef}
          style={{
            fontSize: isPortraitLayout ? 30 : 24,
            lineHeight: isPortraitLayout ? '33px' : '26px',
            color: isPortraitLayout ? '#000000' : '#2a2c3f',
          }}
        >
          {hm.second}S
        </span>
      </p>
      <img
        ref={imageRef}
        className={styles.image}
        src={hm.heatMap}
        onLoad={() => {
          setIsImageLoaded(true)
        }}
      />
    </div>
  )
}

export default PptPageHeatMapFrame
